import { useStore } from '@nanostores/react'
import clsx from 'clsx'
import React, {
  type FC,
  type PropsWithChildren,
  useMemo,
  useState,
} from 'react'

import { Modal } from '@/components/Modal/Modal'
import { NextWeekMenuFlyOut } from '@/components/NextWeekMenu/NextWeekMenuFlyOut'
import { SectionRichText } from '@/components/Sections/SectionRichText/Base'
import { $isLoggedIn } from '@/stores/account/computed'
import type { ReactLinkProps } from '@/types/components/link'
import { getLinkAttributes } from '@/utilities/link'

import useMediaQuery from '@/hooks/useMediaQuery'
import config from 'tailwind.config'

import './link.scss'

export const Link: FC<PropsWithChildren<ReactLinkProps>> = ({
  link,
  modalCloseButtonType,
  classes,
  onClick,
  children,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const isUserLoggedIn = useStore($isLoggedIn)
  const isDesktopScreen = useMediaQuery(
    `(min-width: ${config.theme.screens.lg})`,
    true,
  )

  const { isModal: isModalContent, modalContent, nextweekmenu } = link
  const isModal = isModalContent || !!nextweekmenu

  const { href, additionalParams } = useMemo(
    () => getLinkAttributes(link, isUserLoggedIn),
    [link, isUserLoggedIn],
  )

  const hasHeaderContent = modalContent?.heading || modalContent?.description

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      onClick(event)
      return
    }

    if (isModal) {
      event.preventDefault()
      setIsModalOpen(!isModalOpen)
    }
  }

  return (
    <>
      <a
        href={href}
        className={clsx(classes)}
        onClick={handleClick}
        {...additionalParams}
      >
        {(modalContent?.content || nextweekmenu) && isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            closeButtonType={modalCloseButtonType}
            onClose={() => {
              setIsModalOpen(false)
            }}
          >
            <div className="link-modal__wrapper">
              {nextweekmenu ? (
                <div className="modal__content">
                  <NextWeekMenuFlyOut nextweekmenu={nextweekmenu} />
                </div>
              ) : (
                <>
                  {hasHeaderContent && (
                    <header className="modal__header">
                      {modalContent?.heading && (
                        <h2
                          className={`modal__heading ${
                            isDesktopScreen ? 'type-h4' : 'type-h3'
                          }`}
                        >
                          {' '}
                          {modalContent.heading}
                        </h2>
                      )}
                      {modalContent?.description && (
                        <p className="type-body">{modalContent.description}</p>
                      )}
                    </header>
                  )}
                  <div className="modal__content">
                    {modalContent?.content && (
                      <SectionRichText content={modalContent.content} />
                    )}
                  </div>
                </>
              )}
            </div>
          </Modal>
        )}
        {children}
      </a>
    </>
  )
}
